import type { Stage } from "./display";
import { Event, KeyboardEvent } from "./events";

/** Port of the game's Key helper; keyCodes match flash/AS3 (= legacy DOM). */
export class Key {
  static initialize(_stage: Stage | null): void {
    if (Key.initialized) return;
    Key.initialized = true;
    window.addEventListener("keydown", (e) => {
      keysDown.add(e.keyCode);
      if (SWALLOWED_KEYS.has(e.keyCode)) e.preventDefault();
      keyStage?.dispatchEvent(
        new KeyboardEvent(KeyboardEvent.KEY_DOWN, e.keyCode),
      );
    });
    window.addEventListener("keyup", (e) => {
      keysDown.delete(e.keyCode);
      keyStage?.dispatchEvent(
        new KeyboardEvent(KeyboardEvent.KEY_UP, e.keyCode),
      );
    });
    window.addEventListener("blur", () => {
      keysDown.clear();
      keyStage?.dispatchEvent(new Event(Event.DEACTIVATE));
    });
  }

  static isDown(keyCode: number): boolean {
    return keysDown.has(keyCode);
  }

  /** True while any key at all is held; the intro card uses it as a skip cue. */
  static anyDown(): boolean {
    return keysDown.size > 0;
  }

  private static initialized = false;
}

export function bindKeyStage(stage: Stage): void {
  keyStage = stage;
}

/**
 * flash.net.SharedObject on top of localStorage. `data` is a plain object so
 * ported code can keep using `so.data.foo = x; so.flush()`.
 */
export class SharedObject {
  data: Record<string, unknown> = {};

  static getLocal(name: string): SharedObject {
    let instance = SharedObject.instances.get(name);
    if (!instance) {
      instance = new SharedObject(name);
      SharedObject.instances.set(name, instance);
    }
    return instance;
  }

  /** Flash reports 0 for a never-written object; the game uses it to detect a fresh save. */
  get size(): number {
    return localStorage.getItem(this.storageKey) === null
      ? 0
      : Object.keys(this.data).length;
  }

  flush(): string {
    localStorage.setItem(this.storageKey, JSON.stringify(this.data));
    return "flushed";
  }

  private constructor(name: string) {
    this.storageKey = `xenos.so.${name}`;
    const raw = localStorage.getItem(this.storageKey);
    if (raw) {
      try {
        const parsed: unknown = JSON.parse(raw);
        if (parsed && typeof parsed === "object")
          this.data = parsed as Record<string, unknown>;
      } catch {
        // corrupt save; start fresh rather than blocking boot
      }
    }
  }

  private static readonly instances = new Map<string, SharedObject>();

  private readonly storageKey: string;
}

export class URLRequest {
  constructor(public url = "") {}
}

export function navigateToURL(request: URLRequest, target = "_blank"): void {
  window.open(request.url, target);
}

// flash.ui.ContextMenu has no web analogue; the game only uses it for
// right-click credits, so these keep the ported constructor code intact.
export class ContextMenuItem {
  constructor(
    public caption = "",
    public separatorBefore = false,
  ) {}
  addEventListener(_type: string, _fn: (e: unknown) => void): void {}
}

export class ContextMenu {
  customItems: ContextMenuItem[] = [];
  hideBuiltInItems(): void {}
  addEventListener(_type: string, _fn: (e: unknown) => void): void {}
}

export class ContextMenuEvent {
  static readonly MENU_ITEM_SELECT = "menuItemSelect";
}

// Arrows/space scroll the page and would fight the game's own handling.
const SWALLOWED_KEYS = new Set([32, 37, 38, 39, 40, 17, 16]);

const keysDown = new Set<number>();

let keyStage: Stage | null = null;
